/**
 * AgentTaskDetail - Full view: report, tools called, tool results
 * Easy-to-read layout for completed research tasks
 */

import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/cjs/styles/prism";
import { AgentToolCallsBar } from "./AgentToolCallsBar";
import "./AgentTaskDetail.css";

const getApiBase = () => {
    if (process.env.NODE_ENV === "production") {
        return process.env.REACT_APP_BACKEND_URL || "";
    }
    return process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
};

export interface AgentTaskDetailProps {
    taskId: string;
    title?: string;
    darkMode?: boolean;
    onClose?: () => void;
}

interface LogEntry {
    type: string;
    tool_name?: string;
    tool_input?: Record<string, unknown>;
    tool_output?: string;
    content?: string;
    step_number?: number;
    created_at?: string;
}

interface TaskData {
    id: string;
    title: string;
    status: string;
    result_summary?: string;
    logs?: LogEntry[];
}

function stripTaskComplete(text: string): string {
    const t = text.trim();
    const match = t.match(/^TASK_COMPLETE\s*/i);
    if (match) {
        return t.slice(match[0].length).trimStart();
    }
    return t;
}

export const AgentTaskDetail: React.FC<AgentTaskDetailProps> = ({
    taskId,
    title: propTitle,
    darkMode = false,
    onClose,
}) => {
    const [task, setTask] = useState<TaskData | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!taskId) return;

        const fetchTask = async () => {
            try {
                const res = await fetch(
                    `${getApiBase()}/api/agent/tasks/${encodeURIComponent(taskId)}?include=logs`
                );
                if (!res.ok) throw new Error("Failed to fetch");
                const data = await res.json();
                setTask(data);
                setError(null);
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to load");
            }
        };

        fetchTask();
        const interval = setInterval(fetchTask, 3000);
        return () => clearInterval(interval);
    }, [taskId]);

    if (error) {
        return (
            <div className={`agent-task-detail ${darkMode ? "dark" : ""}`}>
                <div className="agent-task-detail-error">Error: {error}</div>
            </div>
        );
    }

    if (!task) {
        return (
            <div className={`agent-task-detail ${darkMode ? "dark" : ""}`}>
                <div className="agent-task-detail-loading">Loading...</div>
            </div>
        );
    }

    const title = propTitle || task.title;

    // Pair tool_call with following tool_result (they appear in sequence)
    const toolPairs: { call: LogEntry; result?: LogEntry }[] = [];
    const logs = task.logs || [];
    for (let i = 0; i < logs.length; i++) {
        if (logs[i].type === "tool_call") {
            const result = logs[i + 1]?.type === "tool_result" && logs[i + 1].tool_name === logs[i].tool_name
                ? logs[i + 1]
                : undefined;
            toolPairs.push({ call: logs[i], result });
        }
    }

    return (
        <div className={`agent-task-detail ${darkMode ? "dark" : ""}`}>
            <div className="agent-task-detail-header">
                <h2 className="agent-task-detail-title">{title}</h2>
                <span className={`agent-task-detail-status status-${task.status}`}>
                    {task.status.replace("_", " ")}
                </span>
                {onClose && (
                    <button
                        type="button"
                        className="agent-task-detail-close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        ×
                    </button>
                )}
            </div>

            <div className="agent-task-detail-body">
                {task.result_summary && (
                    <section className="agent-task-detail-section">
                        <h3>Report</h3>
                        <div className="agent-task-detail-report agent-task-detail-report-scroll">
                            <div className="agent-task-detail-markdown">
                                <ReactMarkdown
                                    components={{
                                        code({ node, className, children, ...props }) {
                                            const match = /language-(\w+)/.exec(className || "");
                                            const content = String(children).replace(/\n$/, "");
                                            const isInline = !match && !content.includes("\n");
                                            if (isInline) {
                                                return <code className={className} {...props}>{children}</code>;
                                            }
                                            return (
                                                <SyntaxHighlighter
                                                    style={oneDark as Record<string, React.CSSProperties>}
                                                    language={match ? match[1] : "text"}
                                                    PreTag="div"
                                                    customStyle={{ margin: "0.5em 0", borderRadius: "6px", fontSize: "0.85em" }}
                                                >
                                                    {content}
                                                </SyntaxHighlighter>
                                            );
                                        },
                                    }}
                                >
                                    {stripTaskComplete(task.result_summary)}
                                </ReactMarkdown>
                            </div>
                        </div>
                    </section>
                )}
            </div>
            <AgentToolCallsBar toolPairs={toolPairs} darkMode={darkMode} />
        </div>
    );
};
